import { Cell, CellWithHierarchy } from '@pelckmans/elementis/components/table';
import { useCallback, useContext, useState } from 'react';
import { useTranslation } from 'react-i18next';
import useLicenses from '../../../../../hooks/useLicenses';
import HorizontalScrollContextProps from '../../../../../shared-components/table/context/HorizontalScrollContext';
import ModuleContext from '../../../../material/_contexts/ModuleContext';
import type { StudentAssignment } from '../hooks/useStudentAssignments';
import Assignment from './Assignment';

const BASE = 'PAGES.ASSIGNMENTS.STUDENT.SECTION.TABLE.BODY.ASSIGNMENT_DIVISIONS';

export type EndDateCategory = 'TODAY' | 'THIS_WEEK' | 'LATER' | 'LAST_WEEK' | 'LAST_TWO_WEEKS' | 'IN_LAST_MONTH';

type AssignmentDivisionProps = {
  endDateCategory: EndDateCategory;
  assignments: StudentAssignment[];
  monthAndYear?: string;
  finished?: boolean;
  fetchAssignments: () => Promise<void>;
};

function AssignmentDivision({
  endDateCategory,
  assignments,
  monthAndYear,
  finished,
  fetchAssignments,
}: AssignmentDivisionProps) {
  const [collapsed, setCollapsed] = useState(false);
  const { t } = useTranslation();

  const { module, subModules } = useContext(ModuleContext);
  const { horizontalScroll } = useContext(HorizontalScrollContextProps);
  const { isModuleLicensed } = useLicenses();

  const isEmpty = assignments.length === 0;
  const hasOne = assignments.length === 1;

  const getAssignmentModule = useCallback(
    (assignment: StudentAssignment) => {
      if (!module) {
        throw new Error('No module in context');
      }

      const assignmentModule = [module, ...subModules].find(m => m!.id === assignment.moduleId);

      if (!assignmentModule) {
        throw new Error(`No module found for id ${assignment.moduleId}`);
      }

      return assignmentModule;
    },
    [module, subModules],
  );

  return (
    <>
      <tr>
        <CellWithHierarchy
          highlighted
          hierarchyProps={{
            level: 0,
            onToggleCollapsed: () => setCollapsed(x => !x),
            collapsed,
            collapseButtonLabelText: t(`${BASE}.COLLAPSE_BUTTON.A11Y_LABEL`),
          }}
          colspan={5}
        >
          <Cell.Text
            title={t(`${BASE}.END_DATE.${endDateCategory}`, { monthAndYear })}
            subTitle={{
              text: hasOne
                ? t(`${BASE}.ONE_ASSIGNMENT`)
                : t(`${BASE}.ASSIGNMENTS_COUNT`, { count: assignments.length }),
              position: 'bottom',
            }}
          />
        </CellWithHierarchy>
        <Cell highlighted className="actions-cell" sticky={horizontalScroll} />
      </tr>
      {!collapsed && isEmpty && (
        <tr>
          <CellWithHierarchy hierarchyProps={{ level: 1, hasCollapsibleParent: true }} colspan={5}>
            <Cell.Message title={t(`${BASE}.EMPTY_MESSAGE.${endDateCategory}`)} />
          </CellWithHierarchy>
          <Cell sticky={horizontalScroll} className="actions-cell" />
        </tr>
      )}
      {!collapsed &&
        !isEmpty &&
        assignments.map(assignment => {
          const assignmentModule = getAssignmentModule(assignment);
          const isLicensed = isModuleLicensed(assignmentModule);

          return (
            <Assignment
              key={assignment.id}
              assignment={assignment}
              endDateCategory={endDateCategory}
              finished={finished}
              fetchAssignments={fetchAssignments}
              assignmentModule={assignmentModule}
              disabled={assignment.materialType === 'medialink' && !isLicensed}
            />
          );
        })}
    </>
  );
}

export default AssignmentDivision;
